import {
  DELETE_ATO_CAMPAIGN_BANNER_ASYNC,
  DISABLE_PHISHING_SOC_TRIAL_ASYNC,
  ENABLE_PHISHING_SOC_TRIAL_ASYNC,
  GENERATE_STATUS_TOKEN_ASYNC,
  GET_AGENT_STATUS_BATCH_ASYNC,
  GET_AGENT_TOKEN_ASYNC,
  GET_ATO_CAMPAIGN_BANNER_ASYNC,
  GET_BILLING_STATUS_ASYNC,
  GET_PHISHING_SOC_TRIAL_ASYNC,
  REQUEST_THEMIS_SUMMARY_ASYNC,
} from 'containers/Agents/constants'
import { FETCH_APP_DATA_ASYNC } from 'containers/App/constants'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { LIST_REQUEST_ASYNC } from 'containers/ResolutionCenter/List/constants'
import { UPDATE_MONTHLY_LIMIT_ASYNC } from 'containers/Settings/CreditsManagement/constants'

const initialState = {
  agentToken: null,
  agentHostname: null,
  agentTokenExpiresAt: null,
  statusToken: null,
  statusHostname: null,
  statusTokenExpiresAt: null,
  agentStatusBatch: null,
  billingStatus: null,
  // Previous PAYG monthly limit (paygCredits.total) stashed on the optimistic
  // monthly-limit update so an ERROR can roll billingStatus back.
  billingLimitPrev: null,
  phishingSocTrial: null,
  showMeBanner: false,
  getPriceBanner: false,
  isCampaignActive: false,
  showAtoCampaignBanner: false,
  incidentIdToEidMap: {},
  incidentIdToReportType: {},
  incidentIdWithThemisSummary: null,
  loading: {
    agentToken: false,
    agentStatusToken: false,
    agentStatusBatch: false,
    billingStatus: false,
    phishingSocTrial: false,
    themisSummary: false,
  },
  error: {
    agentToken: null,
    agentStatusToken: null,
    agentStatusBatch: null,
    billingStatus: null,
    phishingSocTrial: null,
    themisSummary: null,
  },
}

/**
 * Immutably sets the PAYG monthly limit (stored on paygCredits.total) on the
 * billing/status data, leaving other credit figures intact. Returns the data
 * unchanged when it is null.
 */
const setPaygMonthlyLimit = (data, monthlyLimit) =>
  data ? { ...data, paygCredits: { ...(data.paygCredits ?? {}), total: monthlyLimit } } : data

export default (state = initialState, action) => {
  switch (action.type) {
    // Agent Token
    case GET_AGENT_TOKEN_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, agentToken: true },
        error: { ...state.error, agentToken: null },
      }
    case GET_AGENT_TOKEN_ASYNC.SUCCESS:
      return {
        ...state,
        agentToken: action.payload.agentToken,
        agentHostname: action.payload.agentHostname,
        agentTokenExpiresAt: action.payload.expiresAt,
        loading: { ...state.loading, agentToken: false },
        error: { ...state.error, agentToken: null },
        ...(action.payload.eid &&
          action.payload.reportId && {
            incidentIdToEidMap: {
              ...state.incidentIdToEidMap,
              [action.payload.reportId]: action.payload.eid,
            },
            incidentIdToReportType: {
              ...state.incidentIdToReportType,
              [action.payload.reportId]: action.payload.reportType,
            },
          }),
      }
    case GET_AGENT_TOKEN_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, agentToken: false },
        error: { ...state.error, agentToken: action.error },
      }
    case GET_AGENT_TOKEN_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, agentToken: false },
      }

    // Generate Agent Status Token (generic)
    case GENERATE_STATUS_TOKEN_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, agentStatusToken: true },
        error: { ...state.error, agentStatusToken: null },
      }
    case GENERATE_STATUS_TOKEN_ASYNC.SUCCESS:
      return {
        ...state,
        statusToken: action.payload.agentToken,
        statusHostname: action.payload.agentHostname,
        statusTokenExpiresAt: action.payload.expiresAt,
        loading: { ...state.loading, agentStatusToken: false },
        error: { ...state.error, agentStatusToken: null },
      }
    case GENERATE_STATUS_TOKEN_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, agentStatusToken: false },
        error: { ...state.error, agentStatusToken: action.error },
      }
    case GENERATE_STATUS_TOKEN_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, agentStatusToken: false },
      }
    case LIST_REQUEST_ASYNC.INITIATE:
      return {
        ...state,
        eidsToFetch: [],
      }
    case LIST_REQUEST_ASYNC.CANCEL:
      return {
        ...state,
        eidsToFetch: [],
      }
    case LIST_REQUEST_ASYNC.ERROR:
      return {
        ...state,
        eidsToFetch: [],
        error: { ...state.error, list: action.error },
      }
    case LIST_REQUEST_ASYNC.SUCCESS:
      return {
        ...state,
        incidentIdToEidMap: action.payload.data.reports.results.reduce(
          (acc, result) => {
            if (result.eid) {
              acc[result.id] = result.eid
            }
            return acc
          },
          { ...state.incidentIdToEidMap }
        ),
        incidentIdToReportType: action.payload.data.reports.results.reduce(
          (acc, result) => {
            if (result.eid && result.reportType) {
              acc[result.id] = result.reportType
            }
            return acc
          },
          { ...state.incidentIdToReportType }
        ),
      }

    // Agent Status Batch
    case GET_AGENT_STATUS_BATCH_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, agentStatusBatch: true },
        error: { ...state.error, agentStatusBatch: null },
      }
    case GET_AGENT_STATUS_BATCH_ASYNC.SUCCESS:
      return {
        ...state,
        agentStatusBatch: action.payload,
        loading: { ...state.loading, agentStatusBatch: false },
        error: { ...state.error, agentStatusBatch: null },
      }
    case GET_AGENT_STATUS_BATCH_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, agentStatusBatch: false },
        error: { ...state.error, agentStatusBatch: action.error },
      }
    case GET_AGENT_STATUS_BATCH_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, agentStatusBatch: false },
      }

    // App (re)load: same stale-error cleanup as the company switch above, so a
    // page refresh after a transient 403 also lets the poll restart.
    case FETCH_APP_DATA_ASYNC.SUCCESS:
      return {
        ...state,
        error: { ...state.error, agentStatusToken: null, agentStatusBatch: null },
      }

    // Billing / Status — the single source for the credit balance.
    case GET_BILLING_STATUS_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, billingStatus: true },
        error: { ...state.error, billingStatus: null },
      }
    case GET_BILLING_STATUS_ASYNC.SUCCESS:
      return {
        ...state,
        billingStatus: action.payload,
        loading: { ...state.loading, billingStatus: false },
        error: { ...state.error, billingStatus: null },
      }
    case GET_BILLING_STATUS_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, billingStatus: false },
        error: { ...state.error, billingStatus: action.error },
      }
    case GET_BILLING_STATUS_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, billingStatus: false },
      }

    // MSP company switch: drop the previous tenant's billing/status so it
    // refetches for the newly-selected company. Also clear a stale
    // agentStatusToken/agentStatusBatch error (e.g. a 403 from the previous
    // company) so getAgentStatusBatchEpic's poll — which stops dead once either
    // error is set — can restart for the newly-selected company instead of
    // staying stuck forever.
    case FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS:
      return {
        ...state,
        billingStatus: null,
        billingLimitPrev: null,
        statusToken: null,
        statusHostname: null,
        statusTokenExpiresAt: null,
        agentStatusBatch: null,
        loading: { ...state.loading, billingStatus: false },
        error: {
          ...state.error,
          billingStatus: null,
          agentStatusToken: null,
          agentStatusBatch: null,
        },
      }

    // Optimistically apply the new monthly limit to billingStatus; ERROR reverts
    // to the stashed previous value. The monthly limit is stored on
    // paygCredits.total in the billing/status shape.
    case UPDATE_MONTHLY_LIMIT_ASYNC.INITIATE:
      return {
        ...state,
        billingStatus: setPaygMonthlyLimit(state.billingStatus, action.payload.monthlyLimit),
        billingLimitPrev: state.billingStatus?.paygCredits?.total ?? null,
      }
    case UPDATE_MONTHLY_LIMIT_ASYNC.SUCCESS:
      return {
        ...state,
        billingStatus: setPaygMonthlyLimit(state.billingStatus, action.payload.monthlyLimit),
        billingLimitPrev: null,
      }
    case UPDATE_MONTHLY_LIMIT_ASYNC.ERROR:
      return {
        ...state,
        billingStatus: setPaygMonthlyLimit(state.billingStatus, state.billingLimitPrev),
        billingLimitPrev: null,
      }

    // Phishing SOC Trial
    case GET_PHISHING_SOC_TRIAL_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: true },
        error: { ...state.error, phishingSocTrial: null },
      }
    case GET_PHISHING_SOC_TRIAL_ASYNC.SUCCESS:
      return {
        ...state,
        phishingSocTrial: action.payload,
        showMeBanner: action.payload?.showMeBanner ?? false,
        getPriceBanner: action.payload?.getPriceBanner ?? false,
        isCampaignActive: action.payload?.isCampaignActive ?? false,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: null },
      }
    case GET_PHISHING_SOC_TRIAL_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: action.error },
      }
    case GET_PHISHING_SOC_TRIAL_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
      }

    // Set Phishing SOC Trial
    case ENABLE_PHISHING_SOC_TRIAL_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: true },
        error: { ...state.error, phishingSocTrial: null },
      }
    case ENABLE_PHISHING_SOC_TRIAL_ASYNC.SUCCESS:
      return {
        ...state,
        phishingSocTrial: action.payload,
        showMeBanner: action.payload?.showMeBanner ?? false,
        getPriceBanner: action.payload?.getPriceBanner ?? false,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: null },
      }
    case ENABLE_PHISHING_SOC_TRIAL_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: action.error },
      }
    case ENABLE_PHISHING_SOC_TRIAL_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
      }

    // Disable Phishing SOC Trial
    case DISABLE_PHISHING_SOC_TRIAL_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: true },
        error: { ...state.error, phishingSocTrial: null },
      }
    case DISABLE_PHISHING_SOC_TRIAL_ASYNC.SUCCESS:
      return {
        ...state,
        phishingSocTrial: action.payload,
        showMeBanner: action.payload?.showMeBanner ?? false,
        getPriceBanner: action.payload?.getPriceBanner ?? false,
        isCampaignActive: action.payload?.isCampaignActive ?? true,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: null },
      }
    case DISABLE_PHISHING_SOC_TRIAL_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
        error: { ...state.error, phishingSocTrial: action.error },
      }
    case DISABLE_PHISHING_SOC_TRIAL_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, phishingSocTrial: false },
      }

    // Fetch Incidents with Themis Summary
    case REQUEST_THEMIS_SUMMARY_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, themisSummary: true },
        error: { ...state.error, themisSummary: null },
      }
    case REQUEST_THEMIS_SUMMARY_ASYNC.SUCCESS:
      return {
        ...state,
        incidentIdWithThemisSummary: action.payload,
        loading: { ...state.loading, themisSummary: false },
        error: { ...state.error, themisSummary: null },
      }
    case REQUEST_THEMIS_SUMMARY_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, themisSummary: false },
        error: { ...state.error, themisSummary: action.error },
      }
    case REQUEST_THEMIS_SUMMARY_ASYNC.CANCEL:
      return {
        ...state,
        incidentIdWithThemisSummary: null,
        loading: { ...state.loading, themisSummary: false },
      }

    // ATO Campaign Banner
    case GET_ATO_CAMPAIGN_BANNER_ASYNC.SUCCESS:
      return {
        ...state,
        showAtoCampaignBanner: action.payload?.showMeBanner ?? false,
      }
    case DELETE_ATO_CAMPAIGN_BANNER_ASYNC.SUCCESS:
    case GET_ATO_CAMPAIGN_BANNER_ASYNC.ERROR:
      return {
        ...state,
        showAtoCampaignBanner: false,
      }

    default:
      return state
  }
}
